import Cookies from 'js-cookie';
import type { Hit as AlgoliaHit } from 'instantsearch.js';
import Image from 'next/image';
import React, { memo, useEffect, useRef } from 'react';
import cn from 'classnames';

import Swatches from '@components/algolia/Hit/Swatches';
import ComparisonCheckbox from '@components/global/Comparison/ComparisonCheckbox';
import RatingStars from '@components/product/ProductRatingStars';
import PromoBadge from '@components/product/PromoBadge';

import { useHitSwatches } from '@hooks/useHitSwatches';

import { pushProductClickToDataLayer } from '@utils/analytics/plpAndSearchDataLayer';
import TagsContainer from '@components/ui/Tags';
import { useUI } from '@context/GlobalUIContext';
import { ProductTagFromHit } from '@adapters/bigcommerce/types/product';
import { ALGOLIA_CHECKOUT_QUERY_ID_NAME } from '@adapters/bigcommerce/constants';
import {
  FEATURE_FLAG_COLOUR_PICKER_V2,
  FEATURE_FLAG_PRODUCT_TAGS,
  FEATURE_FLAG_SHOP_CANADIAN,
  FEATURE_FLAG_MEMBER_PRICING,
  FEATURE_FLAG_QUICK_ADD,
} from '@config/feature-flag';
import PlpColourPicker from '_components/ColourPicker/PlpColourPicker';
import QuickAdd from '_components/QuickAdd';
import { isGiftCardHit } from '@utils/algolia/isGiftCardHit';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { trackProductEvent } from '@utils/gtm/ga4';
import { GA4_EVENT_NAMES } from '@utils/gtm/ga4/constants';
import {
  createDataLayerProduct,
  getList,
} from '@utils/analytics/plpAndSearchDataLayer';

import BuyCanadian from '@components/ui/BuyCanadian';
import MemberPricingDealsBanner from '@components/product/MemberPricingDealsBanner';

import styles from './Hit.module.scss';

/** Also fed to `PlpColourPicker` so hover-preload targets this exact image. */
const HIT_IMAGE_SIZES =
  '(min-width: 1128px) 240px,(min-width: 1000px) 250px,(min-width: 767px) 324px,(min-width: 600px) 191px, (min-width: 327px) 50vw, 100vw';

type BadgeData = {
  code: string;
  discount_percentage: number;
  multi_discount: boolean;
  end_date?: Date | null;
  start_date?: Date | null;
  is_member_price?: boolean;
};

type PriceGroup = {
  price?: number;
  salePrice?: number;
  priceRange?: {
    price?: [number, number];
    salePrice?: [number, number];
  };
};

type PriceGroupKey =
  | 'acmg'
  | 'caffr'
  | 'employee'
  | 'family'
  | 'guest'
  | 'member';

export type PriceGroups = Partial<Record<PriceGroupKey, PriceGroup>>;
export type HitVariant = {
  swatch: string;
  image: string | null;
  imageTransparent: string | null;
  badge?: BadgeData | null;
  badgeGuest?: BadgeData | null;
  price: number;
  prices?: PriceGroups;
  sku: string;
  salePrice: number;
  priceRange?: {
    price?: [number, number];
    salePrice?: [number, number];
  };
  availableOnlinePurchase?: boolean;
  /**
   * Number of size variants for this colour. `1` = single size, so Quick Add
   * auto-adds with no size step; `> 1` sizes the loading skeleton to match.
   * Consumed only to shape the loading placeholder; the fetched product stays
   * authoritative. Supplied per-colour by Algolia (see QuickAdd `variantCount`).
   */
  variantCount?: number;
};

export type HitVariants = {
  [variantName: string]: HitVariant;
};

export type SendEventType = (
  event: string,
  hit: HitProps,
  label: string
) => void;

export type HitProps = AlgoliaHit<{
  availableOnPlp: boolean;
  brand: string;
  badge?: BadgeData;
  badgeGuest?: BadgeData;
  categories: {
    lvl0: string[];
    lvl1: string[];
    lvl2: string[];
    lvl3: string[];
    lvl4: string[];
    lvl5: string[];
    lvl6: string[];
  };
  categoryPageId: string[];
  colour: string;
  dimension5: string;
  gender: string;
  image: string | null;
  imageTransparent: string | null;
  objectID: string;
  parentSku: string;
  price: number;
  prices?: PriceGroups;
  productId: number;
  reviewCount: number;
  reviewScore: number;
  salePrice: number;
  sku: string;
  swatch: string;
  title: string;
  url: string;
  variants: HitVariants;
  weight: number;
  tags: ProductTagFromHit[];
  sendEvent: SendEventType;
  promotions: string[];
  isMadeInCanada?: boolean;
  isCanadianBrand?: boolean;
  isMemberPrice?: boolean;
  isNew?: boolean;
}>;

interface Props {
  hit: HitProps;
  locale?: string;
  isPriority?: boolean;
}

const arePropsEqual = (prevProps: Props, nextProps: Props) => {
  const prevHit = prevProps.hit;
  const nextHit = nextProps.hit;

  const arePriceRangesEqual = (
    prevPriceRange: HitVariant['priceRange'],
    nextPriceRange: HitVariant['priceRange']
  ) => {
    if (!prevPriceRange && !nextPriceRange) {
      return true;
    }
    if (!prevPriceRange || !nextPriceRange) {
      return false;
    }

    const areArraysEqual = (
      prevArray: [number, number] | undefined,
      nextArray: [number, number] | undefined
    ) => {
      if (!prevArray && !nextArray) {
        return true;
      }
      if (!prevArray || !nextArray) {
        return false;
      }
      return prevArray[0] === nextArray[0] && prevArray[1] === nextArray[1];
    };

    return (
      areArraysEqual(prevPriceRange.price, nextPriceRange.price) &&
      areArraysEqual(prevPriceRange.salePrice, nextPriceRange.salePrice)
    );
  };

  const areVariantsEqual = (
    prevVariants: HitVariants,
    nextVariants: HitVariants
  ) => {
    const prevVariantKeys = Object.keys(prevVariants);
    const nextVariantKeys = Object.keys(nextVariants);

    if (prevVariantKeys.length !== nextVariantKeys.length) {
      return false;
    }

    return prevVariantKeys.every((key) => {
      const prevVariant = prevVariants[key];
      const nextVariant = nextVariants[key];

      return (
        prevVariant.swatch === nextVariant.swatch &&
        prevVariant.image === nextVariant.image &&
        prevVariant.price === nextVariant.price &&
        prevVariant.salePrice === nextVariant.salePrice &&
        prevVariant.sku === nextVariant.sku &&
        prevVariant.availableOnlinePurchase ===
          nextVariant.availableOnlinePurchase &&
        arePriceRangesEqual(prevVariant.priceRange, nextVariant.priceRange)
      );
    });
  };

  return (
    prevHit.objectID === nextHit.objectID &&
    prevHit.image === nextHit.image &&
    prevHit.__queryID === nextHit.__queryID &&
    prevHit.url === nextHit.url &&
    prevHit.title === nextHit.title &&
    prevHit.reviewScore === nextHit.reviewScore &&
    prevHit.reviewCount === nextHit.reviewCount &&
    prevHit.parentSku === nextHit.parentSku &&
    (prevHit.tags ?? []).length === (nextHit.tags ?? []).length &&
    (prevHit.tags ?? []).every(
      (tag, index) => tag === (nextHit.tags ?? [])[index]
    ) &&
    areVariantsEqual(prevHit.variants, nextHit.variants)
  );
};

export const Hit = memo(
  ({
    hit,
    locale,
    isPriority = false,
    sendEvent,
  }: {
    hit: HitProps;
    locale?: string;
    isPriority?: boolean;
    sendEvent: SendEventType;
  }) => {
    const { openTagInfoModal } = useUI();
    const dealsBannerRef = useRef<HTMLDivElement>(null);
    const { activeVariant, swatches, prices, priceGroupKey } = useHitSwatches(
      hit.colour,
      hit.variants,
      {
        price: hit.price,
        salePrice: hit.salePrice,
        image: hit.image,
        imageTransparent: hit.imageTransparent,
        badge: hit.badge ?? null,
        badgeGuest: hit.badgeGuest ?? null,
        sku: hit.sku,
      },
      locale
    );

    const algoliaDebug = Cookies.get('algoliaDebug');
    if (algoliaDebug && typeof hit.colour === 'undefined') {
      console.log('hit.colour is undefined', { hit });
    }

    if (algoliaDebug && !activeVariant) {
      console.log('---- no active variant', {
        hitProps: hit,
        activeVariant,
        swatches,
      });
    }

    const hasMemberPriceDifference =
      activeVariant?.hasMemberPriceDifference && FEATURE_FLAG_MEMBER_PRICING;

    const { VIEW_ITEM_LIST } = GA4_EVENT_NAMES;

    const isVisible = useObjectVisibility(dealsBannerRef);

    useEffect(() => {
      if (
        FEATURE_FLAG_MEMBER_PRICING &&
        hasMemberPriceDifference &&
        isVisible
      ) {
        trackProductEvent({
          eventName: VIEW_ITEM_LIST,
          products: [createDataLayerProduct(hit, activeVariant)],
          itemListName: getList(),
        });
      }
    }, [
      hit,
      activeVariant,
      hasMemberPriceDifference,
      isVisible,
      VIEW_ITEM_LIST,
    ]);

    const rawBadge =
      hit.isMemberPrice &&
      FEATURE_FLAG_MEMBER_PRICING &&
      priceGroupKey !== 'guest'
        ? activeVariant?.badge
        : activeVariant?.badgeGuest;
    const activeBadge = rawBadge
      ? { ...rawBadge, isMemberBadge: rawBadge.is_member_price ?? false }
      : null;

    if (
      algoliaDebug &&
      (rawBadge?.code === 'CLEARANCE' || rawBadge?.code === 'SALE') &&
      !activeVariant?.salePrice
    ) {
      console.log('!!! note: sale item does not have sale price <---');
      console.log({ activeVariant });
      console.log({ hit });
    }

    const activeColourKey = activeVariant?.swatchName ?? hit.colour;
    const activeColourAvailableOnlinePurchase =
      hit.variants?.[activeColourKey]?.availableOnlinePurchase;

    const variantQuerySlug = activeVariant?.colourQuerySlug;

    const queryString = new URLSearchParams(
      `${variantQuerySlug ? variantQuerySlug : ''}`
    ).toString();

    const hitUrl = `${hit.url}${
      queryString.length > 0 ? `?${queryString}` : ''
    }`;
    const hitHasSalePrice =
      activeVariant?.salePriceFormatted || activeVariant?.priceRange?.salePrice;
    const hitHasNormalPrice =
      activeVariant?.priceFormatted || activeVariant?.priceRange?.price;
    const hitHasGuestPrice =
      activeVariant?.guestPriceFormatted ||
      activeVariant?.guestPriceRange?.price;
    const shouldShowGuestPriceComparison =
      activeVariant?.shouldShowGuestPriceComparison &&
      FEATURE_FLAG_MEMBER_PRICING;

    // Crossed-out guest price rules:
    // - For guest users: show sale vs base (existing behaviour).
    // - For non-guest groups: always show guest "New Price" as crossed-out original
    //   when we have guest pricing for this variant, regardless of sale/clearance,
    //   but only when guest > current group price (computed in shouldShowGuestPriceComparison).
    const showGuestPriceComparison = !FEATURE_FLAG_MEMBER_PRICING
      ? false
      : shouldShowGuestPriceComparison && hitHasGuestPrice;

    const handleHitClick = (clickedHit: HitProps) => {
      Cookies.set(ALGOLIA_CHECKOUT_QUERY_ID_NAME, clickedHit.__queryID || '');
      pushProductClickToDataLayer(clickedHit, activeVariant);
      sendEvent('click', clickedHit, 'Product Clicked');
    };

    const showTags =
      hit?.tags && hit?.tags.length !== 0 && FEATURE_FLAG_PRODUCT_TAGS;

    const showMapleLeaf = FEATURE_FLAG_SHOP_CANADIAN;

    const hasMemberPriceDifferencePlp =
      FEATURE_FLAG_MEMBER_PRICING &&
      (prices?.member?.price !== prices?.guest?.price ||
        prices?.member?.salePrice !== prices?.guest?.salePrice);

    const hasClearancePrice = activeVariant?.badge?.code === 'CLEARANCE';

    const showMemberPricingDealsBanner =
      hasMemberPriceDifferencePlp && !hasClearancePrice;

    return (
      <article className={styles.hit}>
        {hit.image ? (
          <div className={styles.hitImageContainer}>
            <a
              href={hitUrl}
              className={styles.hitImageLink}
              onClick={() => handleHitClick(hit)}
            >
              {activeVariant?.image || hit?.image ? (
                <Image
                  src={activeVariant?.image || hit?.image || ''}
                  alt={hit.title}
                  className={styles.hitImage}
                  fill
                  sizes={HIT_IMAGE_SIZES}
                  priority={isPriority}
                />
              ) : null}
            </a>
            <PromoBadge
              promotion={activeBadge}
              placement="hit"
              salePrice={activeVariant?.salePrice ?? 0}
            />
            {showMapleLeaf && (hit.isCanadianBrand || hit.isMadeInCanada) ? (
              <div className={styles.buyCanadianWrapper}>
                {hit.isCanadianBrand && !hit.isMadeInCanada ? (
                  <BuyCanadian hit={true} isCanadianBrand={true} />
                ) : null}
                {hit.isMadeInCanada ? (
                  <BuyCanadian hit={true} isMadeInCanada={true} />
                ) : null}
              </div>
            ) : null}
            {/* `availableOnPlp` is not populated on all indices — hide only on explicit false. */}
            {FEATURE_FLAG_QUICK_ADD &&
            hit.availableOnPlp !== false &&
            activeColourAvailableOnlinePurchase !== false &&
            !isGiftCardHit(hit.parentSku) ? (
              <QuickAdd
                productCode={hit.parentSku}
                productTitle={hit.title}
                productUrl={hitUrl}
                initialColour={{
                  colourName: activeColourKey,
                  colourSku: activeVariant?.sku ?? hit.sku,
                }}
                analytics={{ queryID: hit.__queryID }}
              />
            ) : null}
          </div>
        ) : null}

        {FEATURE_FLAG_COLOUR_PICKER_V2 ? (
          <PlpColourPicker
            swatches={swatches}
            preloadImageSizes={HIT_IMAGE_SIZES}
          />
        ) : (
          <Swatches swatches={swatches} />
        )}

        <a
          href={`${hitUrl}#reviews`}
          className={styles.hitReviews}
          onClick={() => pushProductClickToDataLayer(hit, activeVariant)}
        >
          <span>
            <RatingStars rating={hit.reviewScore} />{' '}
          </span>
          <span className={styles.hitReviewCount}>
            ({hit.reviewCount || 0})
          </span>
        </a>

        <a
          href={hitUrl}
          className={styles.hitTitle}
          onClick={() => pushProductClickToDataLayer(hit, activeVariant)}
        >
          {hit.title}
        </a>

        <span className={styles.hitPrices}>
          {/* Main price first: sale price if on sale, otherwise regular price */}
          {hitHasSalePrice ? (
            <span
              className={cn(styles.hitSalePrice, {
                [styles.hitPriceCustomerGroup]:
                  activeVariant?.hasCustomerGroupPriceDifference &&
                  priceGroupKey !== 'member',
              })}
            >
              {activeVariant?.priceRange?.salePrice
                ? activeVariant?.priceRange?.salePrice
                : activeVariant?.salePriceFormatted}
            </span>
          ) : hitHasNormalPrice ? (
            <span
              className={cn(styles.hitPrice, {
                [styles.hitPriceCustomerGroup]:
                  activeVariant?.hasCustomerGroupPriceDifference &&
                  priceGroupKey !== 'member',
              })}
            >
              {activeVariant?.priceRange?.price
                ? activeVariant?.priceRange?.price
                : activeVariant?.priceFormatted}
            </span>
          ) : null}

          {/* Crossed-out price second: guest "original" for member/other groups, or base price for guest sale/clearance */}
          {showGuestPriceComparison && hitHasGuestPrice ? (
            <span className={cn(styles.hitPrice, styles.hitOldPrice)}>
              {activeVariant?.guestPriceRange?.price
                ? activeVariant?.guestPriceRange?.price
                : activeVariant?.guestPriceFormatted}
            </span>
          ) : hitHasSalePrice && hitHasNormalPrice ? (
            <span className={cn(styles.hitPrice, styles.hitOldPrice)}>
              {activeVariant?.priceRange?.price
                ? activeVariant?.priceRange?.price
                : activeVariant?.priceFormatted}
            </span>
          ) : null}
        </span>
        {showMemberPricingDealsBanner && (
          <div className={styles.dealsBannerContainer} ref={dealsBannerRef}>
            <MemberPricingDealsBanner isPlp={true} />
          </div>
        )}
        {showTags && (
          <div className={styles.hitTags}>
            <TagsContainer
              hitId={hit?.objectID ?? 0}
              tags={hit?.tags}
              pdpPath={hitUrl}
              activeVariantSku={activeVariant?.sku}
              clickHandler={openTagInfoModal}
              locale={locale}
            />
          </div>
        )}

        <ComparisonCheckbox product={{ code: hit.parentSku }} />
      </article>
    );
  },
  arePropsEqual
);
